'use client';

// PaginationTable.tsx
import React, { useEffect, useMemo, useRef, useState, MouseEvent, ChangeEvent } from 'react';

// style
import { StyledStickyTable, StyledStickyPagination } from './style';

// libraries
import {
  Checkbox,
  Radio,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  MenuItem,
  Select,
  FormControl,
  SelectChangeEvent
} from '@mui/material';
import { isEmpty, uniqueId } from 'lodash-es';
import { STICKY_TABLE_PAGE_VIEW } from 'constants/pagination';
import { arrowImage } from 'constants/images';

const calculateColumnCount = (columns: Column[]): number => {
  return columns.reduce((acc, column) => {
    if (column.children && column.children.length > 0) {
      return acc + column.children.length;
    }
    return acc + 1;
  }, 0);
};

interface ChildColumn {
  id: string;
  label?: string;
  minWidth?: number;
  maxWidth?: number;
  render?: (value: any, row: any, index: number) => React.ReactNode;
  rowClassName?: string;
}

interface Column extends ChildColumn {
  children?: ChildColumn[];
}

interface PaginationProps {
  current: number;
  pageSize: number;
  total: number;
  totalPages?: number;
  onChange?: (page: number, pageSize: number) => void;
}

type SelectType = 'normal' | 'radio' | 'checkbox';

interface PaginationTableProps {
  columns: Column[];
  dataSource: any[];
  selectType?: SelectType;
  onChange?: (records: any[]) => void;
  onRowClick?: (record: any) => void;
  pagination: PaginationProps;
  onChangePageSize?: (page: number, size: number) => void;
}

export const PaginationTable: React.FC<PaginationTableProps> = ({
  columns,
  dataSource,
  selectType = 'normal',
  onChange,
  onRowClick,
  pagination,
  onChangePageSize
}) => {
  const [colspanLength, setColspanLength] = useState(0);
  const [selectedRecords, setSelectedRecords] = useState<any[]>([]);

  const defaultTablePageSelectView = STICKY_TABLE_PAGE_VIEW.map((item) => ({
    value: item,
    label: String(item)
  }));

  const _dataSource = useMemo(() => {
    const startIndex = (pagination.current - 1) * pagination.pageSize;

    const data = dataSource.map((item, i) => ({
      ...item,
      index: pagination ? startIndex + i + 1 : item.index || i + 1,
      rowKey: uniqueId()
    }));

    return data;
  }, [dataSource, pagination]);

  const handleChangePage = (newPage: number) => {
    const curresnPage = newPage > 0 ? newPage - 1 : 0;

    if (!onChangePageSize) return;
    onChangePageSize(curresnPage, pagination.pageSize);
  };

  const handleChangePageSize = (e: SelectChangeEvent<number>) => {
    const isSelected = Number(e.target.value);
    if (!onChangePageSize) return;

    onChangePageSize(0, isSelected);
  };

  const handleClickRow = (record: any) => {
    if (!onRowClick) return;
    onRowClick(record);
  };

  const handleClickRadioRow = (record: any) => {
    setSelectedRecords([record]);

    if (!onChange) {
      return;
    }
    onChange([record]);
  };

  const handleClickCheckboxRow = (records: any[]) => {
    setSelectedRecords(records);

    if (!onChange) {
      return;
    }
    onChange(records);
  };

  useEffect(() => {
    let totalLength = 0;
    if (selectType === 'checkbox' || selectType === 'radio') {
      totalLength += 1;
    }

    const totalColumns = calculateColumnCount(columns);

    setColspanLength(totalLength + totalColumns);
  }, [selectType, columns]);

  const paginationCount = useMemo(() => {
    const r = pagination.total / pagination.pageSize;

    if (r === 0) {
      return 1;
    }

    return Math.ceil(r);
  }, [pagination]);

  return (
    <StyledStickyTable>
      <Paper sx={{ width: '100%' }}>
        <TableContainer sx={{ maxHeight: 500 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {selectType === 'radio' && (
                  <TableCell padding="checkbox" rowSpan={2}>
                    <Checkbox disabled style={{ visibility: 'hidden' }} />
                  </TableCell>
                )}
                {selectType === 'checkbox' && (
                  <TableCell padding="checkbox" rowSpan={2}>
                    <Checkbox
                      checked={selectedRecords.length === dataSource.length}
                      onChange={(event) => {
                        if (event.target.checked) {
                          handleClickCheckboxRow(_dataSource);
                        } else {
                          handleClickCheckboxRow([]);
                        }
                      }}
                    />
                  </TableCell>
                )}
                {columns.map((column) =>
                  column.children && column.children.length > 0 ? (
                    <TableCell
                      key={column.id}
                      colSpan={column.children.length}
                      align="center"
                      style={{
                        minWidth: column.minWidth,
                        width: column.minWidth,
                        maxWidth: column.maxWidth
                      }}
                    >
                      {column.label}
                    </TableCell>
                  ) : (
                    <TableCell
                      key={column.id}
                      rowSpan={2}
                      style={{
                        minWidth: column.minWidth,
                        width: column.minWidth,
                        maxWidth: column.maxWidth
                      }}
                    >
                      {column.label}
                    </TableCell>
                  )
                )}
              </TableRow>

              <TableRow
                style={{ position: 'sticky', top: '40px', zIndex: 2, backgroundColor: '#f5f5f5' }}
              >
                {columns?.map((column) =>
                  column.children && column.children.length > 0
                    ? column.children.map((childColumn) => (
                        <TableCell
                          key={childColumn.id}
                          align="center"
                          style={{
                            minWidth: childColumn.minWidth,
                            width: childColumn.minWidth,
                            maxWidth: childColumn.maxWidth
                          }}
                        >
                          {childColumn.label}
                        </TableCell>
                      ))
                    : null
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {isEmpty(_dataSource) ? (
                <TableRow>
                  <TableCell colSpan={colspanLength + columns.length}>Data Not Found</TableCell>
                </TableRow>
              ) : (
                _dataSource.map((row, index) => {
                  return (
                    <TableRow
                      hover
                      role="radio"
                      tabIndex={-1}
                      key={row.rowKey}
                      onClick={() => handleClickRow(row)}
                      style={{ cursor: 'pointer' }}
                    >
                      {selectType === 'radio' && (
                        <TableCell padding="checkbox">
                          <Radio
                            value={row.no}
                            checked={selectedRecords.some(
                              (selectedRecord) => selectedRecord.rowKey === row.rowKey
                            )}
                            onClick={(e) => e.stopPropagation()}
                            onChange={() => handleClickRadioRow(row)}
                            inputProps={{ 'aria-label': String(row.no) }}
                          />
                        </TableCell>
                      )}
                      {selectType === 'checkbox' && (
                        <TableCell padding="checkbox">
                          <Checkbox
                            onClick={(e) => e.stopPropagation()}
                            checked={selectedRecords.some(
                              (selectedRecord) => selectedRecord.rowKey === row.rowKey
                            )}
                            onChange={() => {
                              const alreadyContain = selectedRecords.some(
                                (selectedRecord) => selectedRecord.rowKey === row.rowKey
                              );

                              const newRecords = alreadyContain
                                ? selectedRecords.filter(
                                    (selectedRecord) => selectedRecord.rowKey !== row.rowKey
                                  )
                                : [...selectedRecords, row];

                              handleClickCheckboxRow(newRecords);
                            }}
                          />
                        </TableCell>
                      )}
                      {columns.map((column) =>
                        column.children && column.children.length > 0 ? (
                          column.children.map((childColumn) => (
                            <TableCell key={childColumn.id}>
                              {childColumn.render
                                ? childColumn.render(row[childColumn.id], row, index)
                                : row[childColumn.id]}
                            </TableCell>
                          ))
                        ) : column.render ? (
                          <TableCell key={column.id} className={column.rowClassName}>
                            {column.render(row[column.id], row, index)}
                          </TableCell>
                        ) : (
                          <TableCell key={column.id}>{row[column.id]}</TableCell>
                        )
                      )}
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <StyledStickyPagination>
        <div className={`table-pagination`}>
          <div className="table-pagination-select">
            <div className="table-pagination-select-label">
              <p>Items per page</p>
            </div>
            <div className="table-pagination-select-option">
              <FormControl>
                <Select
                  value={pagination.pageSize}
                  onChange={handleChangePageSize}
                  IconComponent={() => (
                    <img src={arrowImage.arrowDropDown} alt="select icon" className="arrowImg" />
                  )}
                >
                  {defaultTablePageSelectView &&
                    defaultTablePageSelectView.map((option, index) => (
                      <MenuItem key={index} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <Pagination
            count={paginationCount}
            page={pagination.current + 1}
            onChange={(_, value) => handleChangePage(value)}
            shape="rounded"
          />
        </div>
      </StyledStickyPagination>
    </StyledStickyTable>
  );
};
